import React, { FC, useState, useEffect } from 'react';
import cx from 'classnames';
import { FixedSizeList as List } from 'react-window';
// BluePrint JS
import { Menu, MenuItem, MenuDivider } from '@blueprintjs/core';
import { Button, TextInput, Popover, Icons } from '@ems/client-design-system';
import { searchIcon, chevronIcon, crossIcon, tickIcon } from './functions/icons';
// interfaces
import { IFilter, IFilterItem, IRow } from './interfaces';
import { getCategoryButton, getNewSelectedItems } from './functions/helper';

export const Filter: FC<IFilter> = ({
  categoryName,
  filterItems,
  selectedItems = [],
  updateItems,
  type = 'multiple',
  languageData,
  iconCategories,
  showClear = true,
  isCategoryNameRequired = true,
  theme,
  showSelectAll = true,
  hasTruncationOptions,
  defaultSearchValue = '',
  disabled = false,
}) => {
  const { noMatchesFound, filterValue, clearValue, all, empty } = languageData;
  const [searchValue, setSearchValue] = useState(defaultSearchValue);
  let canSearch = false;

  const updatedSelectedItems = selectedItems.map(item => {
    const filterItem = filterItems.find(filterItem => filterItem.key === item.key);
    return filterItem ? { ...item, label: filterItem.label } : item;
  });

  const buttonComponent = getCategoryButton(
    categoryName,
    filterItems,
    updatedSelectedItems,
    iconCategories,
    isCategoryNameRequired,
    theme,
    hasTruncationOptions
      ? {
          all: updatedSelectedItems.length === filterItems.length ? all : undefined,
          empty: !updatedSelectedItems.length ? empty : undefined,
        }
      : undefined
  );

  const resetSearchValue = () => {
    setSearchValue(defaultSearchValue);
  };
  const selectItem = (item: IFilterItem) => {
    if (disabled) {
      return;
    }
    updateItems(getNewSelectedItems(item, updatedSelectedItems, type));
  };

  const clearItems = () => {
    if (disabled) {
      return;
    }
    updateItems([]);
  };

  if (filterItems.length >= 20) {
    canSearch = true;
  }

  // Make sure it's a case-insensitive search
  const searchItems = filterItems.filter(({ label }) =>
    label.toLowerCase().includes(searchValue.toLowerCase())
  );

  const isClearDisabled = disabled || updatedSelectedItems.length === 0;

  const noResults = <div className="filter-no-results">{noMatchesFound}</div>;

  const row = ({ index, style }: IRow) => {
    const { key, label, labelElement, icon, className, showDivider, isDisable } = searchItems[
      index
    ];
    const isSelected = updatedSelectedItems.findIndex(item => item.key === key) !== -1;
    const rightIcon = isSelected ? tickIcon : null; // [1]
    const selectItemOnClick = () => selectItem(searchItems[index]);
    const selectItemOnKeyPress = (e: React.KeyboardEvent<HTMLAnchorElement>) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        selectItem(searchItems[index]);
      }
    };
    const createdIcon = (
      <Icons
        iconName={`ic-${icon}`}
        title={`ic-${icon}`}
        className={cx(icon, className)}
        size={24}
        style={
          theme &&
          icon &&
          icon.indexOf('ad-') === 0 && {
            fill: theme[key.toLowerCase()],
            color: theme[key.toLowerCase()],
          }
        }
      />
    );

    return (
      <div style={style}>
        <MenuItem
          className={cx('filter-menu-item', {
            'filter-item-selected': isSelected,
            'filter-empty-value': ['NullValue', 'NotSet'].includes(key),
          })}
          key={`filter-item-${key}`}
          onClick={selectItemOnClick}
          onKeyPress={selectItemOnKeyPress}
          text={labelElement || label}
          labelElement={rightIcon}
          icon={icon ? createdIcon : undefined}
          shouldDismissPopover={false}
          tabIndex={0}
          disabled={disabled || isDisable ? true : false}
        />
        {showDivider && <MenuDivider className="filter-menu-divider" />}
      </div>
    );
  };

  const createFilterMenu = () => {
    const itemCount: number = searchItems.length;
    const listHeight: number = itemCount < 8 ? itemCount * 40 : 300;
    return (
      <Menu className={cx(`${categoryName.toLowerCase()}-filter-menu`)}>
        <List height={listHeight} width={250} itemSize={40} itemCount={itemCount}>
          {row}
        </List>
      </Menu>
    );
  };

  const setSearchOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled) {
      return;
    }
    setSearchValue(e.target.value);
  };

  const [isSelectAllDisabled, setIsSelectAllDisabled] = useState<boolean>(false);
  const selectAllItems = () => {
    if (disabled) {
      return;
    }
    updateItems(filterItems.map(item => ({ label: item.label, key: item.key })));
  };

  useEffect(() => {
    setIsSelectAllDisabled(disabled || updatedSelectedItems.length === filterItems.length);
  }, [updatedSelectedItems, disabled]);
  return (
    <Popover
      position="bottom-left"
      transitionDuration={0}
      autoFocus
      disabled={disabled}
      className={cx('filter-popover', `${categoryName.toLowerCase()}-filter-popover`)}>
      <Button
        className={cx('filter-category-button', `${categoryName.toLowerCase()}-filter-button`)}
        style="subtle"
        rightIcon={chevronIcon}
        aria-label={categoryName + ' filter'}
        onClick={() => {
          if (searchValue !== '' && searchValue !== defaultSearchValue) {
            resetSearchValue();
          }
        }}
        type="button"
        disabled={disabled}>
        {buttonComponent}
      </Button>
      <div
        className={cx(
          'filter-popover-content',
          `${categoryName.toLowerCase()}-filter-popover-content`
        )}>
        {canSearch && (
          <div className="filter-search">
            <TextInput
              icon={searchIcon}
              value={searchValue}
              onChange={setSearchOnChange}
              placeholder={filterValue}
              autoFocus
              disabled={disabled}
            />
          </div>
        )}
        {searchItems.length > 0 ? createFilterMenu() : noResults}
        <div className="filter-options">
          {showSelectAll && (
            <Button style="subtle" onClick={selectAllItems} disabled={isSelectAllDisabled}>
              Select All
            </Button>
          )}
          {showClear && (
            <Button
              style="subtle"
              onClick={clearItems}
              leftIcon={crossIcon}
              disabled={isClearDisabled}>
              {clearValue}
            </Button>
          )}
        </div>
      </div>
    </Popover>
  );
};
